import { SNOWPLOW, PAGE_ID, LoggedInStatus, USER_CONTEXT } from '@/data/constants';
import { EventName, ContextKey, getDataLayerName, initializeSnowplow } from '@sdm/analytics-helper-library';
import { getExistingCartGuid, getLoggedInStatus, getLoggedInUserInfo } from 'Utils/auth';
import { IPCOptimumAccount } from '@ldp/sdm-contexts';
import { IGlobalContextFields } from './analytics.interface';
import {
  IComponentContext,
  IUserContext,
  IPageContext,
  ICartContext,
  IProductContext,
  IAdContext,
} from './snowplow-context.interface';

export const dataLayerName: string = getDataLayerName();

const { PAGE_NAMES, PAGE_TEMPLATES, PAGE_SECTIONS, EVENT_NAMES } = SNOWPLOW;

const { HOME_ELECTRONICS, LANDING, BABY_CHILD, PERSONAL_CARE, HEALTH, BEAUTY } = PAGE_ID;
const {
  HOME_LANDING_PAGE,
  PERSONAL_CARE_LANDING_PAGE,
  HEALTH_LANDING_PAGE,
  ELECTRONICS_LANDING_PAGE,
  BABY_AND_CHILD_LANDING_PAGE,
  BEAUTY_LANDING_PAGE,
} = PAGE_NAMES;

export const initializeAnalytics = () => {
  const isVerbose = process.env.NEXT_PUBLIC_SNOWPLOW_IS_VERBOSE === 'true';
  const snowPlowEnv = process.env.NEXT_PUBLIC_SNOWPLOW_ENV === 'prod' ? SNOWPLOW.ENV.PROD : SNOWPLOW.ENV.DEV;

  const configOptions = {
    enableLinkClickAutoTracking: true,
    isVerbose: isVerbose,
  };

  initializeSnowplow(snowPlowEnv, configOptions);
};

export const mapPageIdToPageName: { [key: string]: string } = {
  [HOME_ELECTRONICS]: ELECTRONICS_LANDING_PAGE,
  [LANDING]: HOME_LANDING_PAGE,
  [BABY_CHILD]: BABY_AND_CHILD_LANDING_PAGE,
  [PERSONAL_CARE]: PERSONAL_CARE_LANDING_PAGE,
  [HEALTH]: HEALTH_LANDING_PAGE,
  [BEAUTY]: BEAUTY_LANDING_PAGE,
};

export const mapPageIdToPageTemplate = (pageId: string) => {
  const departmentLandingPages: string[] = [HOME_ELECTRONICS, BABY_CHILD, PERSONAL_CARE, HEALTH];
  if (departmentLandingPages.includes(pageId)) {
    return PAGE_TEMPLATES.DEPARTMENT_TEMPLATE;
  }
  return null;
};

/**
 * A Snowplow helper function to check whether the event has already been created. This is done by checking for existing events in the window.analyticsLayer with the same name, and whether the latest instance of the event has the same specified data
 *
 * @param {string} eventName - the Snowplow event name
 * @param {string} dataObjectPath - the key or path (in dot notation) within the window.analyticsLayer to the existing object data
 * @param {Object|string} newData - the new data to validate if it is the same as within the existing analyticsLayer object
 * @param {integer} threshold - how many events to look back at
 * @returns {boolean}
 */

export const hasExistingEvent = (eventName: string, dataObjectPath: string, newData: Object | string): boolean => {
  const existingEvents = window?.[dataLayerName]?.filter((analyticsEvent) => analyticsEvent.event === eventName);
  // Get the data in the existing event by traversing the path specified by the dataObjectPath
  return (
    existingEvents?.some((event) => {
      const existingObjectValue = dataObjectPath.split('.').reduce((accumulator, currentValue) => {
        return accumulator?.[currentValue];
      }, event);
      return Boolean(event && JSON.stringify(existingObjectValue) === JSON.stringify(newData));
    }) ?? false
  );
};

export const trackLinkClick = async ({ targetUrl = '' }) => {
  if (window[dataLayerName])
    return window[dataLayerName]?.push({
      event: EventName.LinkClick,
      data: {
        targetUrl,
      },
    });
  return;
};

const getUserContextData = (pcOptimumAccount: IPCOptimumAccount): IUserContext => {
  const { walletId } = pcOptimumAccount || {};
  const { ANONYMOUS, CURRENT, GUEST_LOGGED_IN } = LoggedInStatus;
  const { REGISTRATION_STATUS } = USER_CONTEXT;

  const mapLoginStatus: { [key: string]: string } = {
    [ANONYMOUS]: 'not-logged-in',
    [CURRENT]: 'logged_in',
    [GUEST_LOGGED_IN]: 'guest_logged_in',
  };

  const getUserStatus = (status: string) => {
    if (mapLoginStatus[status]) {
      return mapLoginStatus[status];
    } else {
      return 'not-logged-in';
    }
  };

  const getUserPCId = () => {
    const ciamToken = getLoggedInUserInfo();
    const pcid = ciamToken?.pcid ?? '';
    return pcid;
  };

  const userContextData: IUserContext = {
    pcid_id: getUserPCId(),
    login_status: getUserStatus(getLoggedInStatus()),
    registration_status: walletId ? REGISTRATION_STATUS.REGISTERED : REGISTRATION_STATUS.UNKNOWN,
    pco_wallet_id: walletId ?? null,
  };
  return userContextData;
};

const getCartContextData = (cartCount: number): ICartContext => {
  const guid = getExistingCartGuid();

  const cartContextData = {
    cart_id: guid,
    cart_status: cartCount === 0 ? 'empty' : 'open',
    assortment_type: '',
    parent_cart_id: guid,
  };

  return cartContextData;
};

const getPageContextData = (pageId: string, locale: string, currentDevice: string): IPageContext => {
  const pageName = mapPageIdToPageName[pageId] || pageId;
  const pageContextData = {
    page_name: pageName,
    page_section: PAGE_SECTIONS.LANDING,
    page_language: locale,
    page_template: mapPageIdToPageTemplate(pageId),
    site_type: currentDevice,
    page_url: window.location.href,
  };
  return pageContextData;
};

export const trackSnowplow = (analyticsData: any, globalContextFields: IGlobalContextFields) => {
  const { cartCount, locale, currentDevice, pageId, pcOptimumAccount } = globalContextFields ?? {};

  if (window[dataLayerName])
    window[dataLayerName]?.push({
      ...analyticsData,
      [ContextKey.Cart]: getCartContextData(cartCount),
      [ContextKey.Page]: getPageContextData(pageId, locale, currentDevice),
      [ContextKey.User]: getUserContextData(pcOptimumAccount!),
    });
  return;
};

export const getProductContextData = (context: any): IProductContext => {
  const { brand, code, name, quantity, badges, sponsored, sellerName, prices } = context.productContext;
  const productContextData = {
    product_article_number: '',
    product_upc: code,
    product_name: name,
    product_quantity: quantity,
    product_loyalty_badge: badges?.loyaltyBadge || null,
    product_deal_badge: badges?.dealBadge?.text || '',
    product_text_badge: badges?.textBadge || null,
    product_price: prices.price.value,
    product_was_price: prices.wasPrice?.value,
    product_brand: brand,
    product_sponsor: null,
    product_is_sponsored: sponsored || null,
    product_catalog: null,
    product_seller: sellerName,
    product_position: context.productPosition ?? null,
  };
  return productContextData;
};

export const getProductsArrayContextData = (context: any): IProductContext[] => {
  const productsArray = context.products.map((product: any) => {
    const { code, name, badges, prices, brand, sponsored, productPosition } = product;
    return {
      product_article_number: '',
      product_upc: code,
      product_name: name,
      product_loyalty_badge: badges?.loyaltyBadge || null,
      product_deal_badge: badges?.dealBadge?.text || '',
      product_text_badge: badges?.textBadge || null,
      product_price: prices.price.value,
      product_was_price: prices.wasPrice?.value,
      product_brand: brand,
      product_is_sponsored: sponsored || null,
      product_position: productPosition ?? null,
    };
  });
  return productsArray;
};

export const getAdContextData = (context: any): IAdContext => {
  const {
    serving_id,
    placement_id,
    ad_group_id,
    brand,
    campaign_id,
    creative_id,
    encrypted_cost,
    keyword,
    product_article_number,
  } = context.ad || {};

  const adContextData: IAdContext = {
    serving_id: serving_id || null,
    placement_id: placement_id || null,
    campaign_id: campaign_id || '',
    ad_group_id: ad_group_id || null,
    //cost not supplied by helios
    cost: null,
    encrypted_cost: encrypted_cost || null,
    keyword: keyword || null,
    creative_id: creative_id || null,
    product_article_number: product_article_number || null,
    brand: brand || null,
  };
  return adContextData;
};
const getComponentContextData = (context: any, pageId: string): IComponentContext => {
  const { engine, id, name, number_of_children, type } = context.component;
  const componentContextData = {
    id,
    name,
    type,
    //use page_name value
    placement: mapPageIdToPageTemplate(pageId),
    //position not supplied by helios yet
    position: null,
    number_of_children,
    engine,
  };
  return componentContextData;
};

export const trackCartChanges = (context: any, globalContextFields: IGlobalContextFields) => {
  trackSnowplow(
    {
      event: EVENT_NAMES.PRODUCT_CLEAR,
      [ContextKey.Product]: null,
    },
    { ...globalContextFields }
  );
  trackSnowplow(
    {
      event: EVENT_NAMES.AD_CLEAR,
      [ContextKey.Ad]: null,
    },
    { ...globalContextFields }
  );

  //snowplow events include add_to_cart, increase_quantity, decrease_quantity, remove_from_cart
  trackSnowplow(
    {
      event: context.event,
      [ContextKey.Product]: getProductContextData(context),
      [ContextKey.Ad]: getAdContextData(context),
      [ContextKey.Component]: getComponentContextData(context, globalContextFields.pageId),
    },
    { ...globalContextFields }
  );
};

export const trackProductCarousel = (context: any, globalContextFields: IGlobalContextFields) => {
  const { event } = context;
  //only track carousel events for the same component id once
  if (!hasExistingEvent(context.event, 'component.id', context.component.id)) {
    trackSnowplow(
      {
        event: EVENT_NAMES.COMPONENT_CLEAR,
        [ContextKey.Product]: null,
        [ContextKey.Component]: null,
      },
      { ...globalContextFields }
    );

    //snowplow events include: carousel_load,  carousel_view
    trackSnowplow(
      {
        event,
        [ContextKey.Product]: getProductsArrayContextData(context),
        [ContextKey.Component]: getComponentContextData(context, globalContextFields.pageId),
      },
      { ...globalContextFields }
    );
  }
};

export const trackProduct = (context: any, globalContextFields: IGlobalContextFields) => {
  trackSnowplow(
    {
      event: EVENT_NAMES.COMPONENT_CLEAR,
      [ContextKey.Product]: null,
      [ContextKey.Component]: null,
    },
    { ...globalContextFields }
  );

  //snowplow events include: product_click,
  trackSnowplow(
    {
      event: context.event,
      [ContextKey.Product]: getProductsArrayContextData(context),
      [ContextKey.Component]: getComponentContextData(context, globalContextFields.pageId),
    },
    { ...globalContextFields }
  );
};

export const trackAdEvents = (context: any, globalContextFields: IGlobalContextFields) => {
  //ad_clear event is pushed to analyticsLayer to null any previous ad parameter
  trackSnowplow(
    {
      event: EVENT_NAMES.AD_CLEAR,
      [ContextKey.Ad]: null,
    },
    { ...globalContextFields }
  );
  //ad snowplow events include: ad_load, ad_view, ad_click
  trackSnowplow(
    {
      event: context.event,
      [ContextKey.Ad]: getAdContextData(context),
    },
    { ...globalContextFields }
  );
};
